import React from 'react';
import './css/normalize.css';
import './css/App.css';
import Main from './layout/Main';
import ReactGA from 'react-ga';
import {useTranslation} from 'react-i18next';
import {AppBar, Box, Button, Toolbar, Typography} from '@mui/material';
import {HelpOutline} from '@mui/icons-material';
const TRACKING_ID = 'G-B2B0X7RBSG';

const initializeGA = () => {
	ReactGA.initialize(TRACKING_ID);
};

export const AuthContext = React.createContext();
const initialState = {
	isAuthenticated: false,
	_userId: null,
	cid: null,
	firstName: null,
	lastName: null,
	tid: null,
	email: null,
	systemRole: null,
};
const reducer = (state, action) => {
	switch (action.type) {
		case 'LOGIN':
			localStorage.setItem('_userId', JSON.stringify(action.payload._userId));
			localStorage.setItem('firstName', JSON.stringify(action.payload.firstName));
			localStorage.setItem('lastName', JSON.stringify(action.payload.lastName));
			localStorage.setItem('tid', JSON.stringify(action.payload.tid));
			localStorage.setItem('email', JSON.stringify(action.payload.email));
			localStorage.setItem('systemRole', JSON.stringify(action.payload.systemRole));
			return {
				...state,
				isAuthenticated: true,

				_userId: action.payload._userId,
				firstName: action.payload.firstName,
				lastName: action.payload.lastName,
				tid: action.payload.tid,
				email: action.payload.email,
				systemRole: action.payload.systemRole,
			};
		case 'LOGOUT':
			localStorage.clear();
			return {
				...state,
				isAuthenticated: false,
				_userId: null,
				firstName: null,
				lastName: null,
				tid: null,
				email: null,
			};
		default:
			return state;
	}
};

function App() {
	const {t} = useTranslation();
	const [state, dispatch] = React.useReducer(reducer, initialState);
	const [cookiesAccepted, setCookiesAccepted] = React.useState(() => {
		// Retrieve the user's choice from local storage, null if not set
		const storedValue = localStorage.getItem('cookiesAccepted');
		return storedValue === 'true' ? true : storedValue === 'false' ? false : null;
	});

	React.useEffect(() => {
		const _userId = JSON.parse(localStorage.getItem('_userId') || null);
		const firstName = JSON.parse(localStorage.getItem('firstName') || null);
		const lastName = JSON.parse(localStorage.getItem('lastName') || null);
		const tid = JSON.parse(localStorage.getItem('tid') || null);
		const email = JSON.parse(localStorage.getItem('email') || null);
		const systemRole = JSON.parse(localStorage.getItem('systemRole') || null);

		if (email && tid) {
			dispatch({
				type: 'LOGIN',
				payload: {
					_userId,
					firstName,
					lastName,
					tid,
					email,
					systemRole,
				},
			});
		}
	}, []);

	React.useEffect(() => {
		if (cookiesAccepted) {
			initializeGA();
		}
	}, [cookiesAccepted]);

	const handleAccept = () => {
		localStorage.setItem('cookiesAccepted', 'true');
		setCookiesAccepted(true);
		initializeGA();
	};

	const handleDecline = () => {
		localStorage.setItem('cookiesAccepted', 'false');
		setCookiesAccepted(false);
		// Optional: Remove GA script if already loaded
		removeGAScripts();
	};

	const removeGAScripts = () => {
		// Remove GA scripts from the document
		const gaScript = document.querySelector(`script[src*='https://www.google-analytics.com']`);
		if (gaScript) {
			gaScript.remove();
		}
		// Clear GA cookies
		document.cookie = '_ga=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
		document.cookie = '_gid=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
		document.cookie = '_gat=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
	};

	return (
		<div>
			<AuthContext.Provider
				value={{
					state,
					dispatch,
				}}
			>
				<main className="main">
					<Main />
				</main>
				{cookiesAccepted === null && (
					<Box
						sx={{
							position: 'fixed',
							bottom: {xs: 16, md: 28},
							right: {xs: 16, md: 28},
							zIndex: 1300,
							width: {xs: 'calc(100% - 32px)', sm: 380},
							animation: 'cookieSlideIn 0.5s cubic-bezier(0.16,1,0.3,1)',
							'@keyframes cookieSlideIn': {
								from: {opacity: 0, transform: 'translateY(30px) scale(0.95)'},
								to: {opacity: 1, transform: 'translateY(0) scale(1)'},
							},
						}}
					>
						<Box
							sx={{
								background: 'rgba(24,24,27,0.92)',
								backdropFilter: 'blur(24px)',
								WebkitBackdropFilter: 'blur(24px)',
								borderRadius: '20px',
								boxShadow: '0 20px 60px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.05)',
								p: 3,
								display: 'flex',
								flexDirection: 'column',
								gap: 2.5,
								overflow: 'hidden',
								'&::before': {
									content: '""',
									position: 'absolute',
									top: 0,
									left: 0,
									right: 0,
									height: '3px',
									background: 'linear-gradient(90deg, #4cae52, #81c784, #4cae52)',
									borderRadius: '20px 20px 0 0',
								},
							}}
						>
							{/* Cookie emoji + heading */}
							<Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
								<Box sx={{fontSize: '2.2rem', lineHeight: 1, filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.3))'}}>
									🍪
								</Box>
								<Typography sx={{fontFamily: 'Titillium Web', fontWeight: 700, fontSize: '1.1rem', color: '#fff', letterSpacing: '-0.01em'}}>
									Cookies
								</Typography>
							</Box>

							{/* Description */}
							<Typography sx={{fontFamily: 'Titillium Web', fontWeight: 300, fontSize: '0.88rem', color: 'rgba(255,255,255,0.65)', lineHeight: 1.6}}>
								{t('cookiebanner.text')}
							</Typography>

							{/* Buttons */}
							<Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
								<Button
									onClick={handleAccept}
									fullWidth
									sx={{
										textTransform: 'none',
										fontFamily: 'Titillium Web',
										fontWeight: 600,
										fontSize: '0.9rem',
										color: '#fff',
										background: 'linear-gradient(135deg, #4cae52 0%, #388e3c 100%)',
										borderRadius: '12px',
										py: 1.2,
										boxShadow: '0 4px 16px rgba(76,174,82,0.35)',
										transition: 'all 0.2s ease',
										'&:hover': {
											background: 'linear-gradient(135deg, #56c45c 0%, #43a047 100%)',
											boxShadow: '0 6px 24px rgba(76,174,82,0.5)',
											transform: 'translateY(-1px)',
										},
									}}
								>
									{t('cookiebanner.accept')} ✓
								</Button>
								<Box sx={{display: 'flex', gap: 1}}>
									<Button
										onClick={handleDecline}
										fullWidth
										sx={{
											textTransform: 'none',
											fontFamily: 'Titillium Web',
											fontWeight: 500,
											fontSize: '0.82rem',
											color: 'rgba(255,255,255,0.5)',
											border: '1px solid rgba(255,255,255,0.12)',
											borderRadius: '10px',
											py: 0.9,
											transition: 'all 0.2s ease',
											'&:hover': {
												background: 'rgba(255,255,255,0.06)',
												borderColor: 'rgba(255,255,255,0.25)',
												color: 'rgba(255,255,255,0.8)',
											},
										}}
									>
										{t('cookiebanner.decline')}
									</Button>
									<Button
										onClick={() => window.open('/cookiepolicy')}
										fullWidth
										sx={{
											textTransform: 'none',
											fontFamily: 'Titillium Web',
											fontWeight: 500,
											fontSize: '0.82rem',
											color: 'rgba(255,255,255,0.5)',
											border: '1px solid rgba(255,255,255,0.12)',
											borderRadius: '10px',
											py: 0.9,
											transition: 'all 0.2s ease',
											'&:hover': {
												background: 'rgba(255,255,255,0.06)',
												borderColor: 'rgba(255,255,255,0.25)',
												color: 'rgba(255,255,255,0.8)',
											},
										}}
									>
										{t('cookiebanner.readmore')}
									</Button>
								</Box>
							</Box>
						</Box>
					</Box>
				)}
			</AuthContext.Provider>
		</div>
	);
}

export default App;
